import React, { useState, useMemo, useEffect } from 'react';
import Header from '../../components/ui/Header';
import QuickActionToolbar from '../../components/ui/QuickActionToolbar';

import Icon from '../../components/AppIcon';
import CompanyTable from './components/CompanyTable';
import CompanyModal from './components/CompanyModal';
import CompanyDetailsModal from './components/CompanyDetailsModal';
import SubscriptionModal from './components/SubscriptionModal';
import ConfigurationModal from './components/ConfigurationModal';
import { getCompanies } from '../../services/companyService';

const CompanyManagement = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [subscriptionFilter, setSubscriptionFilter] = useState('all');
  const [sortConfig, setSortConfig] = useState({ column: 'businessName', direction: 'asc' });
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isSubscriptionModalOpen, setIsSubscriptionModalOpen] = useState(false);
  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadCompanies();
  }, []);

  const loadCompanies = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getCompanies();
      setCompanies(data);
    } catch (err) {
      console.error('Error loading companies:', err);
      setError('Failed to load companies. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const filteredAndSortedCompanies = useMemo(() => {
    let filtered = companies?.filter(company => {
      const matchesSearch = 
        company?.businessName?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        company?.legalName?.toLowerCase()?.includes(searchTerm?.toLowerCase()) ||
        company?.taxId?.toLowerCase()?.includes(searchTerm?.toLowerCase());

      const matchesSubscription = subscriptionFilter === 'all' || company?.subscriptionTier?.toLowerCase() === subscriptionFilter?.toLowerCase();

      return matchesSearch && matchesSubscription;
    });

    filtered?.sort((a, b) => {
      let aValue = a?.[sortConfig?.column];
      let bValue = b?.[sortConfig?.column];

      if (sortConfig?.column === 'activeUsers') {
        aValue = parseInt(aValue) || 0;
        bValue = parseInt(bValue) || 0;
      }

      if (aValue < bValue) return sortConfig?.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig?.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [companies, searchTerm, subscriptionFilter, sortConfig]);

  const handleSort = (column) => {
    setSortConfig(prev => ({
      column,
      direction: prev?.column === column && prev?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleAddCompany = () => {
    setSelectedCompany(null);
    setIsAddModalOpen(true);
  };

  const handleEditCompany = (company) => {
    setSelectedCompany(company);
    setIsAddModalOpen(true);
  };

  const handleViewDetails = (company) => {
    setSelectedCompany(company);
    setIsDetailsModalOpen(true);
  };

  const handleManageSubscription = (company) => {
    setSelectedCompany(company);
    setIsSubscriptionModalOpen(true);
  };

  const handleManageConfiguration = (company) => {
    setSelectedCompany(company);
    setIsConfigModalOpen(true);
  };

  const handleDeleteCompany = async (company) => {
    if (window.confirm(`Are you sure you want to delete ${company?.businessName}? This action cannot be undone.`)) {
      try {
        await loadCompanies();
      } catch (err) {
        console.error('Error deleting company:', err);
      }
    }
  };

  const quickActions = [
    {
      label: 'Add Company',
      onClick: handleAddCompany,
      variant: 'default',
      icon: 'Plus'
    },
    {
      label: 'Refresh',
      onClick: loadCompanies,
      variant: 'outline',
      icon: 'RefreshCw'
    }
  ];

  const subscriptionStats = useMemo(() => {
    return {
      total: companies?.length || 0,
      starter: companies?.filter(c => c?.subscriptionTier === 'Starter')?.length || 0,
      professional: companies?.filter(c => c?.subscriptionTier === 'Professional')?.length || 0,
      enterprise: companies?.filter(c => c?.subscriptionTier === 'Enterprise')?.length || 0,
      totalUsers: companies?.reduce((sum, c) => sum + (parseInt(c?.activeUsers) || 0), 0) || 0
    };
  }, [companies]);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <QuickActionToolbar actions={quickActions} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <h1 className="text-3xl font-heading font-bold text-foreground">Company Management</h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Manage multiple company accounts with isolated data, subscription settings, and company-specific configurations
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-card rounded-lg shadow-sm border border-border p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary/10 rounded-md flex items-center justify-center">
                <Icon name="Building2" size={20} color="var(--color-primary)" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Companies</p>
                <p className="text-2xl font-heading font-bold text-foreground">{subscriptionStats?.total}</p>
              </div>
            </div>
          </div>
          <div className="bg-card rounded-lg shadow-sm border border-border p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-success/10 rounded-md flex items-center justify-center">
                <Icon name="Users" size={20} color="var(--color-success)" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Active Users</p>
                <p className="text-2xl font-heading font-bold text-foreground">{subscriptionStats?.totalUsers}</p>
              </div>
            </div>
          </div>
          <div className="bg-card rounded-lg shadow-sm border border-border p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-warning/10 rounded-md flex items-center justify-center">
                <Icon name="Crown" size={20} color="var(--color-warning)" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Enterprise</p>
                <p className="text-2xl font-heading font-bold text-foreground">{subscriptionStats?.enterprise}</p>
              </div>
            </div>
          </div>
          <div className="bg-card rounded-lg shadow-sm border border-border p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-info/10 rounded-md flex items-center justify-center">
                <Icon name="Zap" size={20} color="var(--color-info)" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Professional</p>
                <p className="text-2xl font-heading font-bold text-foreground">{subscriptionStats?.professional}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-card rounded-lg shadow-sm border border-border p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Icon name="Search" size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search companies..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e?.target?.value)}
                  className="w-full pl-10 pr-4 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
            </div>
            <div className="flex gap-2">
              <select
                value={subscriptionFilter}
                onChange={(e) => setSubscriptionFilter(e?.target?.value)}
                className="px-4 py-2 border border-border rounded-md focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="all">All Subscriptions</option>
                <option value="starter">Starter</option>
                <option value="professional">Professional</option>
                <option value="enterprise">Enterprise</option>
              </select>
            </div>
          </div>
        </div>

        {error && (
          <div className="mb-6 bg-error/10 border border-error rounded-lg p-4 flex items-center gap-3">
            <Icon name="AlertCircle" size={20} className="text-error" />
            <p className="text-sm text-error">{error}</p>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Icon name="Loader2" size={32} className="animate-spin text-primary" />
          </div>
        ) : (
          <CompanyTable
            companies={filteredAndSortedCompanies}
            sortConfig={sortConfig}
            onSort={handleSort}
            onViewDetails={handleViewDetails}
            onEdit={handleEditCompany}
            onDelete={handleDeleteCompany}
            onManageSubscription={handleManageSubscription}
            onManageConfiguration={handleManageConfiguration}
          />
        )}
      </div>

      <CompanyModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSubmit={loadCompanies}
        company={selectedCompany}
      />

      <CompanyDetailsModal
        isOpen={isDetailsModalOpen}
        onClose={() => setIsDetailsModalOpen(false)}
        company={selectedCompany}
      />

      <SubscriptionModal
        isOpen={isSubscriptionModalOpen}
        onClose={() => setIsSubscriptionModalOpen(false)}
        onSubmit={loadCompanies}
        company={selectedCompany}
      />

      <ConfigurationModal
        isOpen={isConfigModalOpen}
        onClose={() => setIsConfigModalOpen(false)}
        onSubmit={loadCompanies}
        company={selectedCompany}
      />
    </div>
  );
};

export default CompanyManagement;
